import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { IAPMTransaction, fetchTransactionData } from '../../../libraries/apm';
import { Loader, Table } from '@mantine/core';
import ModalComponent from '../modal';
import ShowReceipt from '../apm-receipt';
import { formatDateTimeLabel } from '../../utils/date';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../context/status-oper-dialog';
import { IconCreditCard } from '@tabler/icons-react';
import './styles.scss';

interface ShowTransactionsProps {
  a001: number;
  movType: string;
  a031: number;
  a032: number;
  a029: number;
  dateTime: string;
}

const ShowTransactionsDetails = ({ a001, movType, a031, a032, a029, dateTime }: ShowTransactionsProps) => {
  const controller = useRef(new AbortController()).current;
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<IAPMTransaction[]>([]);

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (showModal) {
      getTransactionData();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const getTransactionData = () => {
    setLoading(true);

    fetchTransactionData(a001, movType, a031, a032, a029, dateTime, controller)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setMessage(response.data.transactions || []);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
        }
      })
      .catch(err => {
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    controller.abort();
  };

  return (
    <>
      <IconCreditCard stroke={2} onClick={openModal} />
      <ModalComponent
        show={showModal}
        handleClick={closeModal}
        title={lang.TRANSACTIONS}
        content={
          <>
            <Table.ScrollContainer minWidth={500}>
              <Table className={'transactions-table'} withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Td>POS</Table.Td>
                    <Table.Td>{lang.GLOBAL_PERIOD}</Table.Td>
                    <Table.Td>#</Table.Td>
                    <Table.Td>{lang.GLOBAL_DATE_TIME}</Table.Td>
                    <Table.Td>{lang.GLOBAL_AMOUNT}</Table.Td>
                    <Table.Td></Table.Td>
                  </Table.Tr>
                </Table.Thead>

                <Table.Tbody>
                  {loading ? (
                    <Table.Tr>
                      <Table.Td colSpan={7}>
                        <Loader color="#016273" />
                      </Table.Td>
                    </Table.Tr>
                  ) : !message.length ? (
                    <Table.Tr>
                      <Table.Td colSpan={7} style={{ textAlign: 'center' }}>
                        {lang.GLOBAL_NO_RESULTS}
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    message?.map((transaction, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>{transaction.key.a001}</Table.Td>
                        <Table.Td>{transaction.key.a031}</Table.Td>
                        <Table.Td>{transaction.key.a032}</Table.Td>
                        <Table.Td>{formatDateTimeLabel(transaction.dateTime, region)}</Table.Td>
                        <Table.Td>{(transaction.amount / 100).toFixed(2) + ' €'}</Table.Td>

                        <Table.Td>
                          <ShowReceipt
                            a001={transaction.key.a001}
                            movType={transaction.key.movType}
                            a031={transaction.key.a031}
                            a032={transaction.key.a032}
                            a029={transaction.key.a029}
                            dateTime={transaction.key.dateTime}
                          />
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </>
        }
      />
    </>
  );
};

export default ShowTransactionsDetails;
